'use client'
import { FC, useRef } from 'react'
import { Search } from 'lucide-react'
import { cva } from 'class-variance-authority'
import { Input, InputWrapper } from 'design-system'

import { TreeSearchSelectProps } from './types'
import { TreeNodes } from './tree-nodes'
import { cn } from 'design-system'

import { useExpansion } from './use-expansion'
import { useTreeSearch } from './use-tree-search'
import { useTreeNavigation } from './use-tree-navigation'
import { useDisabledNodes } from './use-disabled-nodes'

const treeSearchSelectVariants = cva('', {
  variants: {
    size: {
      sm: 'h-[300px]',
      base: 'h-[450px]',
      l: 'h-[600px]'
    }
  },
  defaultVariants: {
    size: 'base'
  }
})

export const TreeSearchSelect: FC<TreeSearchSelectProps> = ({
  data,
  onSelect,
  size = 'base',
  checkAccessibility = false
}) => {
  // Calculate which nodes should be disabled based on accessibility
  const disabledNodeIds = useDisabledNodes(data, checkAccessibility)

  const {
    searchTerm,
    setSearchTerm,
    isComposing,
    setIsComposing,
    filteredItems,
    rootNodes
  } = useTreeSearch(data)

  const { expandedNodes, toggleNode } = useExpansion(
    filteredItems,
    disabledNodeIds,
    searchTerm
  )

  const resultsRef = useRef<HTMLDivElement>(null)

  const {
    focusedNodeId,
    selectedNodeId,
    handleSelect,
    handleInputKeyDown,
    inputRef,
    setFocusedNodeId
  } = useTreeNavigation({
    rootNodes,
    filteredItems,
    expandedNodes,
    toggleNode,
    onSelect,
    isComposing,
    setSearchTerm,
    resultsRef,
    disabledNodeIds
  })

  return (
    <div className="flex flex-col gap-4">
      <InputWrapper leftIconSlot={<Search />}>
        <Input
          ref={inputRef}
          role="combobox"
          aria-expanded="true"
          aria-controls="search-tree-results"
          aria-activedescendant={focusedNodeId || undefined}
          tabIndex={0}
          onChange={e => setSearchTerm(e.target.value)}
          onKeyDown={handleInputKeyDown}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={() => setIsComposing(false)}
          type="text"
          placeholder="Search organizations..."
          value={searchTerm}
        />
      </InputWrapper>
      <div
        ref={resultsRef}
        id="search-tree-results"
        role="tree"
        className={cn(
          'flex flex-col overflow-y-auto outline-none',
          treeSearchSelectVariants({ size })
        )}
        aria-label="Search results"
        tabIndex={-1}>
        <TreeNodes
          nodes={rootNodes}
          data={filteredItems}
          onSelect={handleSelect}
          searchTerm={searchTerm}
          focusedNodeId={focusedNodeId}
          onFocusNode={setFocusedNodeId}
          expandedNodes={expandedNodes}
          onToggleExpand={toggleNode}
          selectedNodeId={selectedNodeId}
          disabledNodeIds={disabledNodeIds}
        />
      </div>
    </div>
  )
}
